import React, { useState } from 'react';
import { MetaTags } from 'react-meta-tags';
import {
  Container,
  Row,
  Col,
  Button,
  FormGroup,
  Input,
  Spinner,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  CardFooter,
} from 'reactstrap';

import {
  fetchStoreOrderReturn,
  fetchStoreOrderItemsAPI,
  createstoreorder,
} from '../Orders/actions/orderActions';
import { useDispatch } from 'react-redux';
import { Toaster, toast } from 'react-hot-toast';
import { DashBreadcrumb } from '../../components/Common/Breadcrumb';

// Real API call for fetching items for an order
const fetchStoreOrderItems = async ({ orderNo, mobile, dispatch }) => {
  const data = await fetchStoreOrderItemsAPI({
    params: { orderNo, mobile },
    dispatch,
  });
  return data;
};

const CreateFormStoreReturn = () => {
  // Modal state for item creation
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  // Modal form state
  const [returnReason, setReturnReason] = useState('');
  const [resolutionMode, setResolutionMode] = useState('');
  const [comments, setComments] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Handler for opening modal for an item
  const handleCreateItem = (order, item) => {
    setModalData({ order, item });
    setReturnReason('');
    setResolutionMode('');
    setComments('');
    setImageFile(null);
    setModalOpen(true);
  };
  const handleModalClose = () => {
    setModalOpen(false);
    setModalData(null);
    setReturnReason('');
    setResolutionMode('');
    setComments('');
    setImageFile(null);
  };

  // Handle submit for Create Return
  const handleSubmitReturn = async (e) => {
    e.preventDefault();
    if (!returnReason || !resolutionMode) {
      toast.error('Please select return reason and resolution mode.');
      return;
    }
    setSubmitLoading(true);
    try {
      const params = {
        order_id: modalData.order.orderNo,
        item_sku: modalData.item.Product_Code || modalData.item.sku,
        name: modalData.item.Product_Name || modalData.item.name,
        full_name:
          (orderInfo && (orderInfo.CustomerName || orderInfo.customer_name)) ||
          modalData.order.CustomerName ||
          modalData.order.customer_name ||
          '',
        address:
          (orderInfo && (orderInfo.Address || orderInfo.address)) ||
          modalData.order.Address ||
          modalData.order.address ||
          '',
        return_item_qty: modalData.item.Quantity || modalData.item.qty,
        ordered_item_qty: modalData.item.Quantity || modalData.item.qty,
        item_price: modalData.item.Price || modalData.item.price || 0,

        return_reason: returnReason,
        return_comments: comments,
        country: 'ae',
        approved_payment_mode: resolutionMode,
        comments,
        delivery_date:
          modalData.item.Delivery_Date || modalData.item.delivery_date || '',
        delivery_method:
         modalData.item.delivery_method || '',
        delivery_location:
          modalData.item.delivery_location || '',
      };
      // If image, use FormData, else send as JSON
      let payload = params;
      let isFormData = false;
      if (imageFile) {
        isFormData = true;
        const formData = new FormData();
        Object.entries(params).forEach(([k, v]) => formData.append(k, v));
        formData.append('image', imageFile);
        payload = formData;
      }
      await createstoreorder({ params: payload, dispatch });
      toast.success('Store return created successfully!');
      handleModalClose();
      setSearchText('');
      setResults([]);
      setSelectedOrderIdx(null);
      setOrderItems([]);
      setOrderInfo(null);
    } catch (err) {
      toast.error('Failed to create store return.');
    }
    setSubmitLoading(false);
  };
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]); // Multiple orders
  const [selectedOrderIdx, setSelectedOrderIdx] = useState(null); // Selected order index
  const [orderItems, setOrderItems] = useState([]); // Items for selected order
  const [itemsLoading, setItemsLoading] = useState(false);
  const [orderInfo, setOrderInfo] = useState(null); // Customer/order info for selected order
  const dispatch = useDispatch();

  // API call for store return details (pattern from createcstreturnwarehouse.js)
  const [lastQuery, setLastQuery] = useState('');
  const fetchDetails = async (query) => {
    // Prevent duplicate or repeated requests
    if (loading || query === lastQuery) return;
    setLoading(true);
    try {
      // Real API call
      const data = await fetchStoreOrderReturn({
        params: { search: query },
        dispatch,
      });
      if (data && Array.isArray(data.orders)) {
        setResults(
          data.orders.map(order => ({
            orderNo: order.order_no,
            name: order.customer_name,
            status: order.status,
            address: order.address,
            total: order.total,
            orderDate: order.order_date,
            storeCode: order.store_code,
            country: order.country,
          }))
        );
      } else {
        setResults([]);
      }
      setLastQuery(query);
    } catch (err) {
      setResults([]);
    }
    setLoading(false);
  };

  const handleSearch = () => {
    const trimmed = searchText.trim();
    if (trimmed && !loading) {
      fetchDetails(trimmed);
      setSelectedOrderIdx(null); // Reset selection on new search
    }
  };

  // Handler for order click: fetch items for the selected order
  const handleOrderClick = async (idx) => {
    setSelectedOrderIdx(idx);
    setOrderItems([]);
    setOrderInfo(null);
    setItemsLoading(true);
    try {
      const orderNo = results[idx].orderNo;
      // Pass the current searchText as mobile
      const data = await fetchStoreOrderItems({
        orderNo,
        mobile: searchText.trim(),
        dispatch,
      });
      setOrderItems(Array.isArray(data.items) ? data.items : []);
      setOrderInfo(data);
    } catch (err) {
      setOrderItems([]);
      setOrderInfo(null);
    }
    setItemsLoading(false);
  };

  return (
    <>
      <div className="page-content">
        <MetaTags>
          <title>Create Store Returns</title>
        </MetaTags>
        <Container fluid>
          <Row>
            <Col>
              <DashBreadcrumb
                title="Manage Orders"
                breadcrumbItem="Create Store Returns"
              />
            </Col>
          </Row>
          <Row>
            <Col className="p-3 bg-soft-light border-1 border-light">
              <FormGroup
                className="position-relative w-100"
                style={{ maxWidth: 400 }}
              >
                <Input
                  type="text"
                  className="form-control form-control"
                  placeholder="🔍 Enter Mobile Number or Order No..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                />
                <Button
                  color="primary"
                  className="position-absolute top-0 end-0"
                  onClick={handleSearch}
                  disabled={loading}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <span>
                      <i className="bx bx-search-alt align-middle"></i> Search
                    </span>
                  )}
                </Button>
              </FormGroup>
            </Col>
          </Row>
          <Row>
            {loading && (
              <div className="my-3 text-center">
                <Spinner color="primary" />
                <div style={{ fontSize: 14, color: '#888' }}>
                  Loading details...
                </div>
              </div>
            )}
            {results.length > 0 && (
              <>
                <Col md={12}>
                  <Card className="shadow border-0 animate__animated animate__fadeIn">
                    <CardHeader className="text-dark p-2 bg-soft-light">
                      <CardTitle
                        tag="h5"
                        className="mb-0 d-flex align-items-center"
                      >
                        <i className="bx bx-package me-2"></i>
                        <span style={{ fontWeight: 600 }}>
                          {results.length} Orders found with {searchText}
                        </span>
                      </CardTitle>
                    </CardHeader>
                  </Card>
                </Col>
                <Col md={12}>
                  <>
                    <Row>
                      {results.map((order, idx) => {
                        let btnColor = 'outline-primary';
                        if (selectedOrderIdx === idx) {
                          btnColor =
                            order.status === 'delivered' ? 'primary' : 'danger';
                        }
                        return (
                          <Col md={3}>
                            <Card>
                              <CardHeader></CardHeader>
                              <CardBody>
                                <Button
                                  key={order.orderNo}
                                  color={btnColor}
                                  size="md"
                                  onClick={() => handleOrderClick(idx)}
                                  style={{
                                    minWidth: 180,
                                    fontWeight: 600,
                                    boxShadow:
                                      selectedOrderIdx === idx
                                        ? '0 2px 8px #1976d2'
                                        : 'none',
                                    borderRadius: 8,
                                  }}
                                >
                                  <span style={{ fontSize: 17 }}>
                                    {order.orderNo}
                                  </span>
                                  <br />
                                  <span style={{ fontSize: 14, color: '#555' }}>
                                    {order.name}
                                  </span>
                                  <br />
                                  <span
                                    style={{
                                      fontSize: 13,
                                      color:
                                        order.status === 'delivered'
                                          ? '#4caf50'
                                          : '#ff9800',
                                    }}
                                  >
                                    {order.status}
                                  </span>
                                </Button>
                              </CardBody>
                              <CardFooter></CardFooter>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </>
                </Col>
              </>
            )}
            {/* Show items for selected order */}
            {selectedOrderIdx !== null && results[selectedOrderIdx] && (
              <Col md={12}>
                <Card className="my-3 shadow border-0 animate__animated animate__fadeInUp">
                  <CardHeader
                    style={{
                      background: '#f5f6fa',
                      color: '#222',
                      borderRadius: '14px 14px 0 0',
                      boxShadow: '0 2px 12px #e3f2fd',
                      borderBottom: '2.5px solid #e3e6ed',
                      padding: '28px 32px 18px 32px',
                      position: 'relative',
                    }}
                  >
                    <CardTitle
                      tag="h4"
                      className="mb-0 d-flex align-items-center"
                      style={{
                        fontWeight: 800,
                        letterSpacing: 1,
                        fontSize: 22,
                      }}
                    >
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background:
                            'linear-gradient(135deg, #1976d2 60%, #42a5f5 100%)',
                          color: '#fff',
                          borderRadius: '50%',
                          width: 38,
                          height: 38,
                          marginRight: 16,
                          boxShadow: '0 2px 8px #e3f2fd',
                        }}
                      >
                        <i className="bx bx-cube" style={{ fontSize: 22 }}></i>
                      </span>
                      <span
                        style={{
                          fontWeight: 800,
                          color: '#222',
                          textShadow: '0 2px 8px #e3f2fd',
                          textTransform: 'uppercase',
                          letterSpacing: 2,
                        }}
                      >
                        Items in this Order
                      </span>
                      <span
                        style={{
                          fontWeight: 500,
                          fontSize: 15,
                          marginLeft: 18,
                          color: '#1976d2',
                        }}
                      >
                        (Order No: <b>{results[selectedOrderIdx].orderNo}</b>)
                      </span>
                    </CardTitle>
                    <div
                      style={{
                        fontSize: 15,
                        color: '#555',
                        marginTop: 4,
                        fontStyle: 'italic',
                      }}
                    >
                      Review all products below. Click on an item to start a
                      return or view more details.
                    </div>
                  </CardHeader>
                  <CardBody>
                    {/* Customer/Order Info Header */}
                    {orderInfo && (
                      <div
                        style={{
                          marginBottom: 18,
                          background: '#e3f2fd',
                          borderRadius: 8,
                          padding: 16,
                        }}
                      >
                        <div>
                          <b>Customer Name:</b> {orderInfo.CustomerName || '-'}
                        </div>
                        <div>
                          <b>Email ID:</b> {orderInfo.EmailID || '-'}
                        </div>
                        <div>
                          <b>Phone:</b> {orderInfo.MobileNo || '-'}
                        </div>
                        <div>
                          <b>Address:</b> {orderInfo.Address || '-'}
                        </div>
                      </div>
                    )}
                    {itemsLoading ? (
                      <div className="my-3 text-center">
                        <Spinner color="primary" />
                        <div style={{ fontSize: 14, color: '#888' }}>
                          Loading items...
                        </div>
                      </div>
                    ) : orderItems.length > 0 ? (
                      <div className="d-flex flex-wrap gap-3">
                        {orderItems.map((item, i) => (
                          <div
                            key={item.Product_Code + (item.Variant_Code || '')}
                            style={{
                              background:
                                'linear-gradient(90deg, #f6f9fc 60%, #e3f2fd 100%)',
                              borderRadius: 12,
                              boxShadow: '0 2px 12px #e3f2fd',
                              padding: '18px 22px',
                              minWidth: 320,
                              maxWidth: 400,
                              flex: '1 1 320px',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              height: 220,
                              border: '1.5px solid #1976d2',
                              position: 'relative',
                            }}
                          >
                            <div style={{ flex: 1 }}>
                              <div
                                style={{
                                  fontWeight: 700,
                                  fontSize: 18,
                                  color: '#1976d2',
                                  marginBottom: 4,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  maxWidth: 260,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                                title={item.Product_Name}
                              >
                                <i
                                  className="bx bx-cube"
                                  style={{ fontSize: 22, color: '#42a5f5' }}
                                ></i>
                                {item.Product_Name.length > 38
                                  ? item.Product_Name.slice(0, 35) + '…'
                                  : item.Product_Name}
                              </div>
                              <div
                                style={{
                                  fontSize: 15,
                                  color: '#555',
                                  marginBottom: 6,
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>SKU:</span>{' '}
                                <span style={{ color: '#333' }}>
                                  {item.Product_Code}
                                </span>
                                {item.Variant_Code && (
                                  <span
                                    style={{
                                      marginLeft: 10,
                                      color: '#888',
                                      fontSize: 14,
                                    }}
                                  >
                                    | Variant: {item.Variant_Code}
                                  </span>
                                )}
                              </div>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 18,
                                  alignItems: 'center',
                                  marginBottom: 4,
                                }}
                              >
                                <span
                                  style={{
                                    color: '#1976d2',
                                    fontWeight: 600,
                                    fontSize: 15,
                                  }}
                                >
                                  <i className="bx bx-package me-1"></i> Qty:{' '}
                                  {item.Quantity}
                                </span>
                                <span
                                  style={{
                                    color: '#388e3c',
                                    fontWeight: 600,
                                    fontSize: 15,
                                  }}
                                >
                                  <i className="bx bx-money me-1"></i> Price:{' '}
                                  {item.Unit_Price}
                                </span>
                              </div>
                              {item.isReturnable && (
                                <span
                                  style={{
                                    color: '#43a047',
                                    fontWeight: 500,
                                    fontSize: 14,
                                    background: '#e8f5e9',
                                    borderRadius: 6,
                                    padding: '2px 10px',
                                    marginTop: 4,
                                    display: 'inline-block',
                                  }}
                                >
                                  <i className="bx bx-undo"></i> Returnable
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'flex-end',
                                gap: 8,
                                marginTop: 'auto',
                              }}
                            >
                              <Button
                                color="primary"
                                size="sm"
                                style={{
                                  fontWeight: 600,
                                  borderRadius: 6,
                                  minWidth: 120,
                                  boxShadow: '0 2px 8px #1976d2',
                                }}
                                onClick={() =>
                                  handleCreateItem(
                                    results[selectedOrderIdx],
                                    item
                                  )
                                }
                              >
                                <i className="bx bx-plus"></i> Create Return1
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-warning">
                        No items found for this order.
                      </div>
                    )}
                  </CardBody>
                </Card>
              </Col>
            )}
          </Row>
        </Container>
      </div>
      {/*  */}

      {/* Right-side modal for creating return */}
      <Modal
        isOpen={modalOpen}
        toggle={handleModalClose}
        backdrop={true}
        fade={true}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          width: '100vw',
          maxWidth: '420px',
          height: '100vh',
          minHeight: '100vh',
          margin: 0,
          borderRadius: '18px 0 0 18px',
          border: '1.5px solid #e3e6ed',
          zIndex: 2000,
          transition: 'transform 0.3s ease',
          background: '#f8fafc',
          padding: 0,
        }}
        className="modal-right-slide animate__animated animate__fadeInRight"
        contentClassName="h-100 border-0 animate__animated animate__fadeInRight"
      >
        <ModalHeader
          toggle={handleModalClose}
          style={{
            borderBottom: '1.5px solid #e3e6ed',
            fontWeight: 900,
            fontSize: 25,
            background: '#f5f6fa',
            color: '#1976d2',
            borderTopLeftRadius: '18px',
            borderTopRightRadius: 0,
            padding: '28px 36px 18px 36px',
            letterSpacing: 1.5,
            boxShadow: 'none',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(135deg, #1976d2 60%, #42a5f5 100%)',
              color: '#fff',
              borderRadius: '50%',
              width: 38,
              height: 38,
              marginRight: 16,
              boxShadow: '0 2px 8px #e3f2fd',
            }}
          >
            <i className="bx bx-undo" style={{ fontSize: 22 }}></i>
          </span>
          <span
            style={{
              fontWeight: 900,
              color: '#1976d2',
              textTransform: 'uppercase',
              letterSpacing: 2,
            }}
          >
            Create Store Return
          </span>
        </ModalHeader>
        <ModalBody
          style={{
            background: '#f8fafc',
            borderBottomLeftRadius: '18px',
            borderBottomRightRadius: 0,
            padding: '36px 36px 28px 36px',
            minWidth: 380,
            maxWidth: 480,
            minHeight: 420,
            boxShadow: 'none',
            height: 'calc(100vh - 56px)',
            overflowY: 'auto',
          }}
        >
          {modalData && (
            <form
              style={{ display: 'flex', flexDirection: 'column', gap: 22 }}
              onSubmit={handleSubmitReturn}
            >
              <FormGroup style={{ marginBottom: 0 }}>
                <Label
                  style={{ fontWeight: 700, color: '#1976d2', fontSize: 15 }}
                >
                  SKU
                </Label>
                <Input
                  value={modalData.item.Product_Code || modalData.item.sku}
                  disabled
                  style={{
                    background: '#e3f2fd',
                    fontWeight: 600,
                    color: '#1976d2',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 16,
                  }}
                />
              </FormGroup>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label
                  style={{ fontWeight: 700, color: '#1976d2', fontSize: 15 }}
                >
                  Item Name
                </Label>
                <Input
                  value={modalData.item.Product_Name || modalData.item.name}
                  disabled
                  style={{
                    background: '#e3f2fd',
                    fontWeight: 600,
                    color: '#1976d2',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 16,
                    marginBottom: 8,
                  }}
                />
                <Label
                  style={{
                    fontWeight: 700,
                    color: '#1976d2',
                    fontSize: 15,
                    marginTop: 8,
                  }}
                >
                  Price
                </Label>
                <Input
                  value={
                    modalData.item.Price ||
                    modalData.item.price ||
                    modalData.item.Unit_Price ||
                    ''
                  }
                  disabled
                  style={{
                    background: '#e3f2fd',
                    fontWeight: 600,
                    color: '#388e3c',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 16,
                  }}
                />
              </FormGroup>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label
                  style={{ fontWeight: 700, color: '#1976d2', fontSize: 15 }}
                >
                  Quantity
                </Label>
                <Input
                  type="number"
                  min={1}
                  max={modalData.item.Quantity || modalData.item.qty}
                  defaultValue={modalData.item.Quantity || modalData.item.qty}
                  disabled
                  style={{
                    background: '#e3f2fd',
                    fontWeight: 600,
                    color: '#1976d2',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 16,
                  }}
                />
              </FormGroup>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label
                  style={{ fontWeight: 700, color: '#1976d2', fontSize: 15 }}
                >
                  Return Reason
                </Label>
                <Input
                  type="select"
                  value={returnReason}
                  onChange={(e) => setReturnReason(e.target.value)}
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                    borderRadius: 8,
                    border: '1.5px solid #1976d2',
                    background: '#fff',
                  }}
                  required
                >
                  <option value="">Select Reason</option>
                  <option value="Damaged">Damaged</option>
                  <option value="Wrong Item">Wrong Item</option>
                  <option value="Not Needed">Not Needed</option>
                  <option value="Other">Other</option>
                </Input>
              </FormGroup>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label
                  style={{ fontWeight: 700, color: '#1976d2', fontSize: 15 }}
                >
                  Resolution Mode
                </Label>
                <Input
                  type="select"
                  value={resolutionMode}
                  onChange={(e) => setResolutionMode(e.target.value)}
                  style={{
                    fontWeight: 600,
                    fontSize: 16,
                    borderRadius: 8,
                    border: '1.5px solid #1976d2',
                    background: '#fff',
                  }}
                  required
                >
                  <option value="">Select Resolution</option>
                  <option value="Refund">Refund</option>
                  <option value="Credit Note">Credit Note</option>
                </Input>
              </FormGroup>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label
                  style={{ fontWeight: 700, color: '#1976d2', fontSize: 15 }}
                >
                  Upload Image (optional)
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  style={{
                    borderRadius: 8,
                    border: '1.5px solid #1976d2',
                    background: '#fff',
                    fontWeight: 500,
                    fontSize: 15,
                  }}
                  onChange={(e) =>
                    setImageFile(
                      e.target.files && e.target.files[0]
                        ? e.target.files[0]
                        : null
                    )
                  }
                />
              </FormGroup>
              <FormGroup style={{ marginBottom: 0 }}>
                <Label
                  style={{ fontWeight: 700, color: '#1976d2', fontSize: 15 }}
                >
                  Comments
                </Label>
                <Input
                  type="textarea"
                  rows={3}
                  placeholder="Enter comments (optional)"
                  style={{
                    borderRadius: 8,
                    border: '1.5px solid #1976d2',
                    background: '#fff',
                    fontWeight: 500,
                    fontSize: 15,
                  }}
                  value={comments}
                  onChange={(e) => setComments(e.target.value)}
                />
              </FormGroup>
              <Button
                color="primary"
                block
                style={{
                  marginTop: 18,
                  fontWeight: 700,
                  fontSize: 20,
                  borderRadius: 10,
                  boxShadow: '0 2px 12px #1976d2',
                  letterSpacing: 1,
                }}
                type="submit"
                disabled={submitLoading}
              >
                {submitLoading ? (
                  <Spinner size="sm" />
                ) : (
                  <>
                    <i
                      className="bx bx-check-circle"
                      style={{
                        fontSize: 22,
                        marginRight: 8,
                        verticalAlign: 'middle',
                      }}
                    ></i>
                    Submit Return
                  </>
                )}
              </Button>
            </form>
          )}
        </ModalBody>
      </Modal>
      <Toaster position="top-right" reverseOrder={false} />
    </>
  );
};

export default CreateFormStoreReturn;
